<template>
  <RegistrationLayout>
    <template #step-content>
      <div class="flex flex-col items-center gap-6 px-4">
        <h1>{{ t("onboarding.download_app.title") }}</h1>
        <p class="text-xl">{{ t("onboarding.download_app.motivator") }}</p>

        <img
          :src="macbookWithControlaApp"
          class="w-full max-w-md rounded-2xl object-fill"
          alt=""
          height="294"
          width="392"
        />

        <div class="flex w-full max-w-md flex-col items-center">
          <AppDownloadOptions @clicked-download="handleClickedDownload" />

          <button class="mt-14" @click="finish">
            {{ t("onboarding.download_app.download_later.button") }}
          </button>
        </div>
      </div>
    </template>
  </RegistrationLayout>
</template>

<script setup lang="ts">
import { useSeoMeta } from "@unhead/vue";
import macbookWithControlaApp from "shared-ui/assets/macbook-pro-mockup-of-a-man-working-with-controla.avif";
import type { DownloadAssetsSingleAsset } from "shared-ui/types/download-app.ts";
import { useApi } from "shared-ui/utils/api";
import { onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import AppDownloadOptions from "@/components/app-setup/AppDownloadOptions.vue";
import { useActivelyUsingAgentPoll } from "@/composables/use-actively-using-agent-poll.ts";
import RegistrationLayout from "@/layouts/RegistrationLayout.vue";
import { startAppDownload } from "@/utils/app-download.ts";
import { finishDeviceSetup } from "@/utils/device-setup.ts";
import { detectIOSBrowser } from "@/utils/mobile-browser.ts";

const { t } = useI18n();

definePage({
  meta: {
    requiresAuth: true,
  },
});

useSeoMeta({
  title: () => t("onboarding.download_app.title"),
});

const api = useApi();
const router = useRouter();

const retryTimeout = Math.max(
  250,
  Number(import.meta.env.VITE_RETRY_TIMEOUT_MULTIPLIER ?? 1) * 1000,
);
// The app may already be installed, e.g. when it was installed on another
// computer with this OS.
useActivelyUsingAgentPoll(api, retryTimeout, async () => {
  await finish();
  return true;
});

// The button, the poll and the tablet check can overlap; the first run uses up
// the remembered destination, so later runs must not navigate again.
let finishing = false;
async function finish(): Promise<void> {
  if (finishing) return;
  finishing = true;
  await finishDeviceSetup(router);
}

async function handleClickedDownload(
  asset: DownloadAssetsSingleAsset,
): Promise<void> {
  await router.push({
    name: "/setup-device/[os]",
    params: { os: asset.os },
  });
  startAppDownload(asset);
}

onMounted(async () => {
  // There is no app for iOS devices yet.
  if (detectIOSBrowser()) await finish();
});
</script>
